import { UserRoles } from "~enums/userRoles";
import { PermissionFields } from "../enums/permissionFields";
import { PermissionProducts } from "../enums/permissionProducts";
import { PermissionScopeTypes } from "../enums/permissionScopeTypes";
import type { UserRoleConfig, UserRolesConfig } from "../types";

export const DEFAULT_ROLE_CONFIG: UserRoleConfig = {
  canAssignRoles: [],
  requiredFields: new Set(),
  disabledFields: new Set(),
  allowedScopeTypes: new Set(),
  advancedSettingsEnabled: true,
  product: PermissionProducts.Competitions,
};

export const userRolesConfig: UserRolesConfig = {
  [UserRoles.OrganizationAdmin]: {
    canAssignRoles: [
      UserRoles.OrganizationManager,
      UserRoles.LeagueAdmin,
      UserRoles.LeagueManager,
      UserRoles.CompetitionAdmin,
      UserRoles.CompetitionManager,
      UserRoles.SeasonAdmin,
      UserRoles.SeasonManager,
      UserRoles.EntityGroupAdmin,
      UserRoles.EntityGroupManager,
      UserRoles.EntityManager,
      UserRoles.FixtureManager,
      UserRoles.RoleManager,
      UserRoles.SupportObserver,
    ],
    requiredFields: new Set(),
    disabledFields: new Set([PermissionFields.Scope, PermissionFields.SeasonStatuses, PermissionFields.FixtureStatuses]),
    allowedScopeTypes: new Set(),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.OrganizationManager]: {
    canAssignRoles: [],
    requiredFields: new Set(),
    disabledFields: new Set([PermissionFields.Scope, PermissionFields.SeasonStatuses, PermissionFields.FixtureStatuses]),
    allowedScopeTypes: new Set(),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.LeagueAdmin]: {
    canAssignRoles: [
      UserRoles.LeagueManager,
      UserRoles.CompetitionAdmin,
      UserRoles.CompetitionManager,
      UserRoles.SeasonAdmin,
      UserRoles.SeasonManager,
      UserRoles.EntityGroupAdmin,
      UserRoles.EntityGroupManager,
      UserRoles.EntityManager,
      UserRoles.FixtureManager,
      UserRoles.RoleManager,
    ],
    requiredFields: new Set([PermissionFields.Scope]),
    disabledFields: new Set(),
    allowedScopeTypes: new Set([PermissionScopeTypes.League]),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.LeagueManager]: {
    canAssignRoles: [],
    requiredFields: new Set([PermissionFields.Scope]),
    disabledFields: new Set(),
    allowedScopeTypes: new Set([PermissionScopeTypes.League]),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.CompetitionAdmin]: {
    canAssignRoles: [
      UserRoles.CompetitionManager,
      UserRoles.SeasonAdmin,
      UserRoles.SeasonManager,
      UserRoles.EntityGroupAdmin,
      UserRoles.EntityGroupManager,
      UserRoles.EntityManager,
      UserRoles.FixtureManager,
      UserRoles.RoleManager,
    ],
    requiredFields: new Set([PermissionFields.Scope]),
    disabledFields: new Set(),
    allowedScopeTypes: new Set([PermissionScopeTypes.League, PermissionScopeTypes.Competition]),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.CompetitionManager]: {
    canAssignRoles: [],
    requiredFields: new Set([PermissionFields.Scope]),
    disabledFields: new Set(),
    allowedScopeTypes: new Set([PermissionScopeTypes.League, PermissionScopeTypes.Competition]),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.SeasonAdmin]: {
    canAssignRoles: [UserRoles.SeasonManager, UserRoles.EntityManager, UserRoles.FixtureManager, UserRoles.RoleManager],
    requiredFields: new Set([PermissionFields.Scope, PermissionFields.SeasonStatuses]),
    disabledFields: new Set(),
    allowedScopeTypes: new Set([PermissionScopeTypes.League, PermissionScopeTypes.Competition, PermissionScopeTypes.Season]),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.SeasonManager]: {
    canAssignRoles: [],
    requiredFields: new Set([PermissionFields.Scope, PermissionFields.SeasonStatuses]),
    disabledFields: new Set(),
    allowedScopeTypes: new Set([PermissionScopeTypes.League, PermissionScopeTypes.Competition, PermissionScopeTypes.Season]),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.EntityGroupAdmin]: {
    canAssignRoles: [UserRoles.EntityGroupManager, UserRoles.EntityManager],
    requiredFields: new Set([PermissionFields.Scope]),
    disabledFields: new Set([PermissionFields.SeasonStatuses, PermissionFields.FixtureStatuses]),
    allowedScopeTypes: new Set([PermissionScopeTypes.EntityGroup]),
    advancedSettingsEnabled: false,
    product: PermissionProducts.TeamManager,
  },
  [UserRoles.EntityGroupManager]: {
    canAssignRoles: [],
    requiredFields: new Set([PermissionFields.Scope]),
    disabledFields: new Set([PermissionFields.SeasonStatuses, PermissionFields.FixtureStatuses]),
    allowedScopeTypes: new Set([PermissionScopeTypes.EntityGroup]),
    advancedSettingsEnabled: false,
    product: PermissionProducts.TeamManager,
  },
  [UserRoles.EntityManager]: {
    canAssignRoles: [],
    requiredFields: new Set([PermissionFields.Scope]),
    disabledFields: new Set([PermissionFields.SeasonStatuses, PermissionFields.FixtureStatuses]),
    allowedScopeTypes: new Set([PermissionScopeTypes.Entity]),
    advancedSettingsEnabled: false,
    product: PermissionProducts.TeamManager,
  },
  [UserRoles.FixtureManager]: {
    canAssignRoles: [],
    requiredFields: new Set([PermissionFields.Scope, PermissionFields.FixtureStatuses]),
    disabledFields: new Set(),
    allowedScopeTypes: new Set([PermissionScopeTypes.League, PermissionScopeTypes.Competition, PermissionScopeTypes.Season]),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.RoleManager]: {
    canAssignRoles: [],
    requiredFields: new Set([PermissionFields.Scope]),
    disabledFields: new Set(),
    allowedScopeTypes: new Set([PermissionScopeTypes.League, PermissionScopeTypes.Competition, PermissionScopeTypes.Season]),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
  [UserRoles.SupportObserver]: {
    canAssignRoles: [],
    requiredFields: new Set(),
    disabledFields: new Set([PermissionFields.Scope, PermissionFields.SeasonStatuses, PermissionFields.FixtureStatuses]),
    allowedScopeTypes: new Set(),
    advancedSettingsEnabled: true,
    product: PermissionProducts.Competitions,
  },
};
